/** @file readInput: a reader over one command input; every method throws VALIDATION at the field's path. */
import { checkNumber, type NumberRule } from '@zilo/pose3d-format/validate/fields/checkNumber';
import { failField } from '@zilo/pose3d-format/validate/fields/failField';
import type { FieldCheck } from '@zilo/pose3d-format/validate/fields/fieldCheck';
import { USER_NAME_PATTERN } from './userNamePattern';

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * A reader over `input` (an object whose keys are among `keys`) at JSON pointer `path`: `str`, `name` (node or
 * variant name), `num`/`int` (with a format `NumberRule`), `bool`, `arr`, `rec`, `oneOf`, `check` (a format field
 * check), `raw` (any present value), `opt` (`undefined` when absent, else the read) and `nullable` (null stays).
 */
export function readInput(input: unknown, path: string, keys: readonly string[]) {
  const rec = isRecord(input) ? input : failField(path, 'object', input);
  for (const k of Object.keys(rec)) if (!keys.includes(k)) failField(`${path}/${k}`, `one of ${keys.join(', ')}`, rec[k]);
  const at = (k: string): string => `${path}/${k}`;
  const need = (k: string): unknown => rec[k] ?? failField(at(k), 'required field', rec[k]);
  const str = (k: string): string => {
    const v = need(k);
    return typeof v === 'string' && v !== '' ? v : failField(at(k), 'non-empty string', v);
  };
  const num = (k: string, rule: NumberRule = {}): number => {
    const v = need(k);
    checkNumber(rule)(v, at(k));
    return v as number;
  };
  return {
    at, str, num,
    has: (k: string): boolean => rec[k] !== undefined,
    raw: need,
    name: (k: string): string => (USER_NAME_PATTERN.test(str(k)) ? str(k) : failField(at(k), 'a name of letters, digits and _', rec[k])),
    int: (k: string, rule: NumberRule = {}): number => num(k, { ...rule, int: true }),
    bool: (k: string): boolean => (typeof need(k) === 'boolean' ? (rec[k] as boolean) : failField(at(k), 'boolean', rec[k])),
    arr: (k: string): unknown[] => (Array.isArray(need(k)) ? (rec[k] as unknown[]) : failField(at(k), 'array', rec[k])),
    rec: (k: string): Record<string, unknown> => (isRecord(need(k)) ? (rec[k] as Record<string, unknown>) : failField(at(k), 'object', rec[k])),
    oneOf: <T extends string>(k: string, values: readonly T[]): T =>
      (values.includes(need(k) as T) ? (rec[k] as T) : failField(at(k), `one of ${values.join(', ')}`, rec[k])),
    check: (k: string, c: FieldCheck): unknown => {
      c(need(k), at(k));
      return rec[k];
    },
    opt: <T>(k: string, read: (key: string) => T): T | undefined => (rec[k] === undefined ? undefined : read(k)),
    nullable: <T>(k: string, read: (key: string) => T): T | null => (rec[k] === null ? null : read(k)),
  };
}
